import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { HomeService } from '../../../../services/home.service';
import { Item } from '../../../../interfaces/item';
import { Stockitem } from '../../../../interfaces/stockitem';
import { UserService } from '../../../../services/user';
import { DeviceDetectorService } from '../../../../services/device-detector.service';
import { ItemsService } from '../../../../services/items.service';
import { ExcelExportService } from '../../../../services/excel-export.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { PageEvent } from '@angular/material/paginator';
import Swal from 'sweetalert2';
import { WarehouseTypeService } from '../../../../services/warehouse-type';
import { CartService } from '../../../../services/cart';
import { PointOfSaleService } from '../../../../services/pos';
// ============================================================================
// CONSTANTS
// ============================================================================
const PAGINATION_KEY = 'stock_current_page';
const WAREHOUSE_ID_KEY = 'posId';
const LANGUAGE_KEY = 'language';
const DEFAULT_PAGE_SIZE = 10;
const LOW_STOCK_THRESHOLD = 10;
const EXPIRATION_WARNING_MONTHS = 3;
const IMAGE_BASE_URL = 'https://backendproduction.blueboxpet.com/storage/app/public/';

const CATEGORY_NAMES: Record<string, string> = {
  '0': 'soft food', '1': 'soft food can', '2': 'dry food', '3': 'bird food',
  '4': 'medicine', '5': 'supplement', '6': 'accessories', '7': 'toy',
  '8': 'grooming', '9': 'house', '10': 'care', '11': 'treats',
  '12': 'training pads', '13': 'bags', '14': 'cleaning tools', '15': 'litter'
};

const PET_TYPE_NAMES: Record<string, string> = {
  '0': 'adult', '1': 'kitten', '2': 'sterilized', '3': 'persian',
  '4': 'puppy', '5': 'birds', '6': 'other'
};

const PET_NAMES: Record<string, string> = {
  '0': 'cats', '1': 'dogs', '2': 'birds', '3': 'other', '4': 'cats&dogs'
};

// ============================================================================
// COMPONENT
// ============================================================================
@Component({
  selector: 'app-homepage',
  templateUrl: './homepage.component.html',
  styleUrl: './homepage.component.scss',
})
export class HomepageComponent implements OnInit, OnDestroy {

  // ============================================================================
  // STATE: Data & Pagination
  // ============================================================================
  private allData: Stockitem[] = [];
  public totalItems = 0;
  public stockTotal?: string;
  imagePreview: string | null = null;
  selectedImageFile: File | null = null;
  isUploadingImage = false;
  // ============================================================================
  // STATE: UI Flags & Controls
  // ============================================================================
  contentReady = true;
  noData = false;
  searched = false;
  mostSales = false;
  advancedSearch = false;
  isStockTaking = false;
  showRejectPopUp = false;
  cartPopUp = false;
  addImage = false;
  isMobile = false;
  isActive = true;
  AllCategoryactive = true;
  warehouseIsStore = false;
  quantityGuardEnabled = true;

  // ============================================================================
  // STATE: User & Permissions
  // ============================================================================
  private accountType!: string;
  isAdmin!: boolean;
  isSubAdmin!: boolean;
  superAdmin!: boolean;
  public isAr: boolean;

  // ============================================================================
  // STATE: Search & Filter
  // ============================================================================
  searchKey = '';
  private searchTerm: any[] = [];
  filterCards: Item[] = [];

  // ============================================================================
  // STATE: Cart
  // ============================================================================
  cartItems: any[] = [];
  cartItemCount = 0;

  // ============================================================================
  // STATE: Warehouse & Navigation
  // ============================================================================
  private warehouseId!: any | null;
  allWareHouses: any[] = [];
  private newPage = 1;

  // ============================================================================
  // STATE: Table Configuration
  // ============================================================================
  displayedColumns: string[] = [];
  dataSource: MatTableDataSource<Stockitem>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // ============================================================================
  // STATE: Modal & Form Data
  // ============================================================================
  itemId?: string;
  quantity?: string;
  stockImageId!: any;

  // ============================================================================
  // RxJS Cleanup
  // ============================================================================
  private destroy$ = new Subject<void>();

  // ============================================================================
  // CONSTRUCTOR
  // ============================================================================
  constructor(
    private router: Router,
    private homeService: HomeService,
    private userService: UserService,
    public warehouseTypeService: WarehouseTypeService,
    private itemsService: ItemsService,
    private deviceService: DeviceDetectorService,
    private excelExportService: ExcelExportService,
    private cartService: CartService,
    private posService: PointOfSaleService
  ) {
    this.isAr = this.getStoredLanguage();
    this.dataSource = new MatTableDataSource<Stockitem>([]);
    this.initializeDisplayedColumns();
  }

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================
  ngOnInit(): void {
    this.setupDeviceDetection();
    this.loadStoredPagination();
    this.setupCartSubscriptions();
    this.loadUserPermissions();
    this.loadWarehouses();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================================
  // PRIVATE HELPERS: Initialization
  // ============================================================================
  private setupDeviceDetection(): void {
    this.isMobile = this.deviceService.isMobileDevice();
    if (this.isMobile) {
      this.router.navigateByUrl('/Home.moblie');
    }
  }
  public clearSearch(): void {
    this.searchKey = '';
    this.searched = false;
    this.noData = false;
    this.mostSales = false;

    this.resetPagination();
    this.loadStockData();
  }
  private loadStoredPagination(): void {
    this.warehouseId = this.getStoredWarehouseId();
    const savedPage = this.getStoredPage();
    this.newPage = savedPage ?? 1;
  }

  private setupCartSubscriptions(): void {
    this.cartService.cart$
      .pipe(takeUntil(this.destroy$))
      .subscribe(cart => { this.cartItems = cart; });

    this.cartService.cartCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => { this.cartItemCount = count; });
  }

  private loadUserPermissions(): void {
    this.userService.getAccountType()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.processUserPermissions(data['user_type']),
        error: (error) => console.error(error),
      });
  }

  private processUserPermissions(userType: string): void {
    this.accountType = userType;
    this.isAdmin = this.accountType === '1' || this.accountType === '10';
    this.isSubAdmin = this.accountType === '6';
    this.superAdmin = this.accountType === '10';

    this.updateDisplayedColumns();

    if (this.warehouseId) {
      this.loadBusinessSettings(this.warehouseId);
      this.loadStockData();
    } else {
      this.setNoDataState();
    }
  }

  private loadWarehouses(): void {
    this.itemsService.getWearHouses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => this.allWareHouses = res.warehouse,
        error: () => { },
      });
  }

  private loadInitialStockData(): void {
    if (this.warehouseId) {
      this.loadStockData();
    }
  }

  get hasSingleWarehouse(): boolean {
    return this.allWareHouses.length <= 1;
  }

  get canChangeQuantity(): boolean {
    return this.superAdmin || (this.isAdmin && !this.quantityGuardEnabled);
  }

  private loadBusinessSettings(warehouseId: string | number | null): void {
    if (!warehouseId) {
      this.quantityGuardEnabled = true;
      return;
    }

    this.posService.getBusinessSettings(warehouseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const settings = res?.settings ?? res?.business_setting ?? res;
          this.quantityGuardEnabled = this.toBoolean(settings?.quantityguard, true);
        },
        error: () => {
          this.quantityGuardEnabled = true;
        }
      });
  }

  private toBoolean(value: any, fallback: boolean): boolean {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    return value === true || value === 'true' || value === 1 || value === '1';
  }

  // ============================================================================
  // PRIVATE HELPERS: Data Handling
  // ============================================================================
  private updateTableData(data: Stockitem[], total?: number, lastPage?: number): void {
    this.allData = data;
    this.dataSource = new MatTableDataSource<Stockitem>(this.allData);
    this.noData = this.allData.length === 0;
    this.contentReady = false;

    if (total !== undefined) this.stockTotal = String(total);
    if (lastPage !== undefined) this.totalItems = lastPage;

    this.syncPaginatorIndex();
  }

  private syncPaginatorIndex(): void {
    setTimeout(() => {
      if (this.paginator) {
        this.paginator.pageIndex = this.newPage - 1;
      }
    });
  }

  private setNoDataState(): void {
    this.noData = true;
    this.contentReady = false;
  }

  private setLoadingState(loading: boolean): void {
    this.contentReady = loading;
  }

  private resetSearchFlags(): void {
    this.searched = false;
    this.mostSales = false;
  }

  // ============================================================================
  // PRIVATE HELPERS: Column Configuration
  // ============================================================================
  private initializeDisplayedColumns(): void {
    this.displayedColumns = [
      this.isAr ? 'name_ar' : 'name_en',
      'category_id', 'quantity', 'pet_type', 'weight', 'type', 'expiration_data'
    ];
  }

  private updateDisplayedColumns(): void {
    const isWarehouse = this.warehouseTypeService.isWarehouse();

    if ((this.isAdmin || this.isSubAdmin)) {
      this.displayedColumns = this.getAdminColumns(isWarehouse);
    }

    if (!isWarehouse && !(this.isAdmin || this.isSubAdmin)) {
      this.addUserSpecificColumns();
    }
  }

  private getAdminColumns(isWarehouse: boolean): string[] {
    const base = [
      this.isAr ? 'name_ar' : 'name_en',
      'pet_type',
      'category_id',
      'quantity',
      'weight',
      'type',
      'expiration_data'
    ];

    if (!this.isSubAdmin) {
      base.push('mostSales');
    }

    if (!isWarehouse) {
      if (
        this.warehouseTypeService.isShop() ||
        this.warehouseTypeService.isShopAndClinic()
      ) {
        base.push('price', 'pricea');
      } else {
        base.push('price', 'affiliationprice', 'pricea', 'priceb', 'pricec');
      }
    } else {
      base.push('price');
    }

    base.push('delete');

    return base;
  }
  private addUserSpecificColumns(): void {
    const isShopLike =
      this.warehouseTypeService.isShop() ||
      this.warehouseTypeService.isShopAndClinic();

    if (isShopLike) {
      if (!this.displayedColumns.includes('price')) {
        this.displayedColumns.push('price');
      }

      if (!this.displayedColumns.includes('pricea')) {
        this.displayedColumns.push('pricea');
      }

      if (!this.displayedColumns.includes('delete')) {
        this.displayedColumns.push('delete');
      }

      return;
    }

    const additions: Record<string, string[]> = {
      '2': ['affiliationprice'],
      '3': ['pricea'],
      '4': ['priceb'],
      '5': ['pricec']
    };

    if (additions[this.accountType]) {
      additions[this.accountType].forEach(col => {
        if (!this.displayedColumns.includes(col)) {
          this.displayedColumns.push(col);
        }
      });

      if (!this.displayedColumns.includes('delete')) {
        this.displayedColumns.push('delete');
      }
    }
  }

  // ============================================================================
  // PRIVATE HELPERS: Storage
  // ============================================================================
  private getStoredLanguage(): boolean {
    return localStorage.getItem(LANGUAGE_KEY) === 'ar';
  }

  private getStoredWarehouseId(): string | null {
    return localStorage.getItem(WAREHOUSE_ID_KEY);
  }

  private getStoredPage(): number | null {
    const saved = localStorage.getItem(PAGINATION_KEY);
    return saved ? Number(saved) : null;
  }

  private savePage(page: number): void {
    localStorage.setItem(PAGINATION_KEY, String(page));
  }

  // ============================================================================
  // PRIVATE HELPERS: UI & Formatting
  // ============================================================================
  getCategoryName(categoryId: string): string {
    return CATEGORY_NAMES[categoryId] ?? '-';
  }

  getPetTypeName(petType: string): string {
    return PET_TYPE_NAMES[petType] ?? '-';
  }

  getPetName(pet: string): string {
    return PET_NAMES[pet] ?? '-';
  }

  formatDate(date: string): string {
    if (!date) return '-';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear().toString().slice(2);
    return `${day}-${month}-${year}`;
  }

  isLowStock(quantity: number): boolean {
    return quantity <= LOW_STOCK_THRESHOLD;
  }

  isExpiringSoon(date: string): boolean {
    if (!date) return false;
    const today = new Date();
    const inputDate = new Date(date);
    const diffInTime = inputDate.getTime() - today.getTime();
    const diffInMonths = diffInTime / (1000 * 3600 * 24 * 30);
    return diffInMonths < EXPIRATION_WARNING_MONTHS;
  }

  fixImage(url: any): string {
    return IMAGE_BASE_URL + url;
  }

  getQuantityDisplay(element: Stockitem): { value: string | number; isWarning: boolean } {
    if (Number(element.quantity) === 0) {
      return { value: 'out of stock', isWarning: false };
    }
    const isWarning = this.isLowStock(Number(element.quantity)) && (this.isAdmin || this.isSubAdmin);
    return { value: element.quantity, isWarning };
  }

  getExpirationDisplay(element: Stockitem): { value: string; isWarning: boolean } {
    const formatted = this.formatDate(element.expiration_data);
    const isWarning = (this.isAdmin || this.isSubAdmin) && this.isExpiringSoon(element.expiration_data);
    return { value: formatted, isWarning };
  }

  getPriceColumnHeader(columnKey: string): string {
    const isShopLike =
      this.warehouseTypeService.isShop() ||
      this.warehouseTypeService.isShopAndClinic();

    if (isShopLike) {
      const shopHeaders: Record<string, string> = {
        'price': 'Wholesale Price',
        'pricea': 'Selling price'
      };

      return shopHeaders[columnKey] ?? columnKey;
    }

    if (this.isAdmin || this.isSubAdmin) {
      const headers: Record<string, string> = {
        'affiliationprice': 'Affiliation',
        'pricea': 'Page A',
        'priceb': 'B',
        'pricec': 'C',
        'price': 'price'
      };

      return headers[columnKey] ?? 'price';
    }

    return 'price';
  }

  // ============================================================================
  // PRIVATE HELPERS: API Wrappers
  // ============================================================================
  private loadStockData(): void {
    this.setLoadingState(true);
    this.resetSearchFlags();

    this.homeService.getoneWareHouse(this.warehouseId, this.newPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.updateTableData(
            data.stocks['data'],
            data.stocks['total'],
            data.stocks['last_page']
          );
        },
        error: (error) => this.handleError(error),
      });
  }

  private loadTopSalesData(page: number): void {
    if (this.isSubAdmin) return;

    this.setLoadingState(true);
    this.resetSearchFlags();
    this.mostSales = true;

    this.homeService.getStocksByWarehouse(this.warehouseId, page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.updateTableData(
            data['data'],
            data['total'],
            data['last_page']
          );
        },
        error: (error) => this.handleError(error),
      });
  }
  private performSearch(addToCartFirst = false): void {
    if (!this.searchKey.trim() || !this.warehouseId) return;

    this.setLoadingState(true);
    this.mostSales = false;

    this.homeService.searchByNameAndWarehouse(this.searchKey, this.warehouseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.updateTableData(data);
          this.searched = true;

          if (addToCartFirst && data[0]) {
            this.addToCart(data[0]);
          }

          this.setLoadingState(false);
        },
        error: (error) => this.handleError(error),
      });
  }
  private handleError(error: any): void {
    console.error(error);
    this.setLoadingState(false);
  }

  // ============================================================================
  // PUBLIC ACTIONS: Pagination & Warehouse
  // ============================================================================
  applyWareHouse(id: any): void {
    this.warehouseId = id;
    this.loadBusinessSettings(this.warehouseId);
    this.savePage(1);
    this.resetPagination();
    this.loadStockDataForPage(1);
    this.cartService.clearCart();
    this.cartItems = [];
    this.cartItemCount = 0;
  }

  resetPagination(): void {
    if (this.paginator) this.paginator.firstPage();
    this.newPage = 1;
    this.savePage(1);
  }

  onPaginateChange(event: PageEvent): void {
    const newPage = event.pageIndex + 1;
    this.newPage = newPage;
    this.savePage(newPage);

    if (this.mostSales) {
      this.loadTopSalesData(newPage);
    } else {
      this.loadStockDataForPage(newPage);
    }
  }

  private loadStockDataForPage(page: number): void {
    this.setLoadingState(true);
    this.resetSearchFlags();

    this.homeService.getoneWareHouse(this.warehouseId, page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.updateTableData(
            data.stocks['data'],
            data.stocks['total'],
            data.stocks['last_page']
          );
        },
        error: (error) => this.handleError(error),
      });
  }

  // ============================================================================
  // PUBLIC ACTIONS: Stock Operations
  // ============================================================================
  toggleOffer(id: number): void {
    this.setLoadingState(true);
    this.homeService.toggleDailyOffer(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          Swal.fire({ icon: 'success', title: 'تم تحديث المنتج بنجاح' });
          this.loadStockData();
        },
        error: (err) => {
          this.setLoadingState(false);
          console.error(err.error.message);
        }
      });
  }

  stockTaking(): void {
    this.setLoadingState(true);
    this.homeService.getSimpleStockByWarehouse(this.warehouseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stocks) => {
          this.isStockTaking = true;
          this.displayedColumns = [this.isAr ? 'name_ar' : 'name_en', 'quantity'];
          this.updateTableData(stocks);
        },
        error: (err) => console.error(err),
      });
  }

  getAllStock(): void {
    this.resetPagination();
    this.setLoadingState(true);
    this.resetSearchFlags();

    this.homeService.getAllItems()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.allData = data.reverse();
          this.stockTotal = String(data.length);
          this.updateTableData(this.allData);
        },
        error: (error) => this.handleError(error),
      });
  }

  getAllStockData(perpage: any, page: any): void {
    this.setLoadingState(true);
    this.resetSearchFlags();

    this.homeService.getStock(perpage, page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.updateTableData(
            data['data'],
            data['total'],
            data['last_page']
          );
        },
        error: (error) => this.handleError(error),
      });
  }

  getwarehouse(id: any, page: any): void {
    this.setLoadingState(true);
    this.resetSearchFlags();

    this.homeService.getoneWareHouse(id, page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.updateTableData(
            data.stocks['data'],
            data.stocks['total'],
            data.stocks['last_page']
          );
        },
        error: (error) => this.handleError(error),
      });
  }

  getwarehouseBytopSale(page: any): void {
    if (this.isSubAdmin) return;

    this.setLoadingState(true);
    this.resetSearchFlags();
    this.mostSales = true;

    this.homeService.getStocksByWarehouse(this.warehouseId, page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.updateTableData(
            data['data'],
            data['total'],
            data['last_page']
          );
        },
        error: (error) => this.handleError(error),
      });
  }

  updateQtyFromStock(id: any, qty: any): void {
    this.setLoadingState(true);
    this.homeService.updateQtyFromStock(id, qty)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadStockData(),
        error: (err) => console.log(err),
      });
  }

  deleteitem(id: string): void {
    Swal.fire({
      title: this.isAr ? 'متأكد؟؟' : 'Are You Sure?',
      showCancelButton: true,
      confirmButtonText: this.isAr ? 'اه' : 'YES',
      cancelButtonText: this.isAr ? 'لا' : 'no'
    }).then((result) => {
      if (result.isConfirmed) {
        this.setLoadingState(true);
        this.homeService.delete(id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => this.loadStockData(),
            error: (err) => console.log(err),
          });
      }
    });
  }

  // ============================================================================
  // PUBLIC ACTIONS: Search & Filter
  // ============================================================================
  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource.filter = filterValue;
  }

  onEnterPressed(): void {
    console.log('User pressed Enter!');
    this.barcodeSearch();
  }

  barcodeSearch(): void {
    this.performSearch(true);
  }

  search(): void {
    if (!this.searchKey || !this.searchKey.trim()) {
      this.clearSearch();
      return;
    }

    if (this.searchKey.trim().length < 2) {
      Swal.fire({
        icon: 'warning',
        title: 'يجب كتابة 2 حروف على الأقل للبحث',
        confirmButtonText: 'حسناً'
      });
      return;
    }

    this.performSearch(false);
  }
  // ============================================================================
  // PUBLIC ACTIONS: Cart
  // ============================================================================
  addToCart(item: any): void {
    this.cartService.addOrToggleItem(item, 'product');
  }

  removeFromCart(item: any): void {
    this.cartService.removeItem(item);
    if (this.cartService.getCart().length === 0) {
      this.cartPopUp = false;
      localStorage.removeItem("cart");
      localStorage.removeItem("cartWarehouseId");
    }
  }

  isInCart(item: any): boolean {
    return this.cartItems.some(cartItem => cartItem.id === item.id && (cartItem.cartItemType || 'product') === 'product');
  }



  quickOrder(): void {
    const posId = Number(localStorage.getItem('posId'));

    this.userService.getWarehouseType(posId).subscribe({
      next: ({ warehouses_type }) => {
        const type = Number(warehouses_type);

        if ([2, 3, 4].includes(type)) {
          this.router.navigate(['/Addorderstore'], {
            queryParams: {
              cart: JSON.stringify(this.cartItems)
            }
          });
          return;
        }

        else if (type === 1) {
          this.router.navigate(['/Addorderwarehouse'], {
            queryParams: {
              cart: JSON.stringify(this.cartItems)
            }
          });
          return;
        }

        else {
          this.router.navigate(['/Addorder'], {
            queryParams: {
              cart: JSON.stringify(this.cartItems)
            }
          });
          return;
        }
      },
      error: (err) => console.error(err),
    });
  }
  cartPopUpOpen(): void {
    this.cartPopUp = true;
  }

  // ============================================================================
  // PUBLIC ACTIONS: Modal & Image
  // ============================================================================
  changeQuantity(item: any): void {
    this.showRejectPopUp = true;
    this.itemId = item;
  }

  close(): void {
    this.showRejectPopUp = false;
    this.cartPopUp = false;
    this.addImage = false;

    this.imagePreview = null;
    this.selectedImageFile = null;
    this.stockImageId = null;
    this.quantity = undefined;
  }

  onSubmit(): void {

    const hasQuantityValue = this.quantity !== null && this.quantity !== undefined && String(this.quantity).trim() !== '';
    const qty = Number(this.quantity);

    if (!hasQuantityValue || isNaN(qty)) {
      Swal.fire({
        icon: 'error',
        title: 'Please enter a valid number'
      });
      return;
    }

    if (qty < 0) {
      Swal.fire({
        icon: 'error',
        title: 'Quantity cannot be negative'
      });
      return;
    }

    if (!Number.isInteger(qty)) {
      Swal.fire({
        icon: 'error',
        title: 'Quantity must be a whole number'
      });
      return;
    }

    if (this.itemId) {
      this.updateQtyFromStock(this.itemId, qty);
    }

    this.close();
  }

  openAddImage(id: any): void {
    this.stockImageId = id;
    this.addImage = true;
    this.imagePreview = null;
    this.selectedImageFile = null;
  }


  onImageSelected(event: any): void {
    const file = event.target.files?.[0];
    if (!file) return;

    this.selectedImageFile = file;

    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreview = reader.result as string;
    };
    reader.readAsDataURL(file);
  }
  submitImage(): void {
    if (!this.selectedImageFile || !this.stockImageId) {
      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'من فضلك اختر صورة أولاً' : 'Please select an image first'
      });
      return;
    }

    this.isUploadingImage = true;

    this.homeService.updateStockImage(this.stockImageId, this.selectedImageFile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isUploadingImage = false;

          Swal.fire({
            icon: 'success',
            title: this.isAr ? 'تم تحديث الصورة بنجاح' : 'Image updated successfully'
          });

          this.imagePreview = null;
          this.selectedImageFile = null;
          this.stockImageId = null;
          this.addImage = false;

          this.loadStockData();
        },
        error: () => {
          this.isUploadingImage = false;

          Swal.fire({
            icon: 'error',
            title: this.isAr ? 'حدث خطأ أثناء تحديث الصورة' : 'An error occurred while updating the image'
          });
        },
      });
  }
  // ============================================================================
  // PUBLIC ACTIONS: Navigation & Export
  // ============================================================================
  edit(itemobj: any): void {
    this.router.navigate(['/AddItem'], { queryParams: itemobj });
  }

  clone(itemobj: any): void {
    this.router.navigate(['/AddItem'], {
      queryParams: { ...itemobj, isClone: true }
    });
  }

  exportToExcel(): void {
    this.excelExportService.exportToExcel('stocktable', 'BlueBox Stock', ['delete']);
  }
}
